import React, { useEffect, useMemo, useState } from 'react';
import { AiOutlineSearch } from 'react-icons/ai';
import {
    GOAL_DATE_MAX,
    GOAL_DATE_MIN,
    GOAL_TYPE,
    buildGoalDefaults,
    createGoal,
    formatDateRange,
    formatMoney,
    isDateStillBeingTyped,
    normalizeAmountInput,
    salespersonLabel,
    sanitizeAmountInput,
    validateGoal,
    validateGoalDates,
} from '../../services/goalsService';
import styles from '../../css/Goals.module.css';

const MAX_CUSTOMER_RESULTS = 6;
const CUSTOMER_SEARCH_MAX_LENGTH = 100;

const emptyFields = (today) => ({ targetAmount: '', startDate: today, endDate: today, lastGoal: null });

/**
 * Crear una meta. Con initial.targetNo el objetivo ya viene elegido (atajos de búsqueda y tablas).
 * Al elegir un objetivo, el formulario se precarga con su última meta (ver buildGoalDefaults).
 */
export default function GoalFormDialog({ idCompany, data, initial, onClose, onSaved }) {
    const locked = Boolean(initial && initial.targetNo);
    const [goalType, setGoalType] = useState((initial && initial.goalType) || GOAL_TYPE.salesperson);
    const [targetNo, setTargetNo] = useState((initial && initial.targetNo) || '');
    const [customerQuery, setCustomerQuery] = useState('');
    const [fields, setFields] = useState(() => (
        locked ? buildGoalDefaults(initial.goalType, initial.targetNo, data.goals, data.today) : emptyFields(data.today)
    ));
    const [errors, setErrors] = useState({});
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const onKeyDown = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [onClose, saving]);

    const chooseTarget = (type, no) => {
        setTargetNo(no);
        setErrors({});
        setFields(no ? buildGoalDefaults(type, no, data.goals, data.today) : emptyFields(data.today));
    };

    const changeType = (type) => {
        setGoalType(type);
        setCustomerQuery('');
        chooseTarget(type, '');
    };

    const selectedCustomer = goalType === GOAL_TYPE.customer && targetNo
        ? data.customers.find((c) => c.customerNo === targetNo)
        : null;

    const customerResults = useMemo(() => {
        const q = customerQuery.trim().toLowerCase();
        if (!q) return [];
        return data.customers.filter((c) => c.searchText.includes(q)).slice(0, MAX_CUSTOMER_RESULTS);
    }, [customerQuery, data.customers]);

    const lockedName = () => {
        if (!locked) return '';
        if (goalType === GOAL_TYPE.customer) return selectedCustomer ? selectedCustomer.name : targetNo;
        const salesperson = data.salespeople.find((s) => s.salespersonNo === targetNo);
        return salesperson ? salespersonLabel(salesperson) : targetNo;
    };

    const buildPayload = (current) => ({
        goalType,
        targetNo,
        targetAmount: current.targetAmount,
        startDate: current.startDate,
        endDate: current.endDate,
    });

    const changeAmount = (text) => {
        setFields((current) => ({ ...current, targetAmount: sanitizeAmountInput(text) }));
        setErrors((current) => ({ ...current, targetAmount: undefined }));
    };

    const finishAmount = () => {
        const next = { ...fields, targetAmount: normalizeAmountInput(fields.targetAmount) };
        setFields(next);
        const found = validateGoal(buildPayload(next), data.goals);
        setErrors((current) => ({ ...current, targetAmount: found.targetAmount }));
    };

    // Inicio después del fin o un traslape se avisan al momento, no hasta guardar.
    const changeDate = (name, value) => {
        const next = { ...fields, [name]: value };
        setFields(next);
        const found = validateGoalDates(buildPayload(next), data.goals);
        setErrors((current) => ({
            ...current,
            startDate: isDateStillBeingTyped(next.startDate) ? undefined : found.startDate,
            endDate: isDateStillBeingTyped(next.endDate) ? undefined : found.endDate,
        }));
    };

    const finishDate = () => {
        const found = validateGoalDates(buildPayload(fields), data.goals);
        setErrors((current) => ({ ...current, startDate: found.startDate, endDate: found.endDate }));
    };

    // Enter en la búsqueda elige el primer resultado; sin esto enviaría el formulario incompleto.
    const onCustomerSearchKeyDown = (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        if (customerResults.length > 0) chooseTarget(goalType, customerResults[0].customerNo);
    };

    const save = async (e) => {
        e.preventDefault();
        const payload = buildPayload(fields);
        const found = validateGoal(payload, data.goals);
        setErrors(found);
        setFormError('');
        if (Object.keys(found).length > 0) return;

        setSaving(true);
        try {
            onSaved(await createGoal(idCompany, data.scope, payload));
        } catch (err) {
            if (err.fieldErrors) setErrors(err.fieldErrors);
            else setFormError(err.message);
            setSaving(false);
        }
    };

    const fieldError = (name) => errors[name] && (
        <p id={`goal-${name}-error`} className={styles.fieldError} role="alert">{errors[name]}</p>
    );
    const invalidProps = (name) => (errors[name]
        ? { 'aria-invalid': true, 'aria-describedby': `goal-${name}-error` }
        : {});

    const renderTypePicker = () => (
        <fieldset className={styles.typePicker}>
            <legend className={styles.label}>Goal for</legend>
            <div className={styles.segmented}>
                {[[GOAL_TYPE.salesperson, 'Salesperson'], [GOAL_TYPE.customer, 'Customer']].map(([value, label]) => (
                    <label key={value} className={`${styles.segment} ${goalType === value ? styles.segmentActive : ''}`}>
                        <input type="radio" name="goal-type" className={styles.srOnly} checked={goalType === value} onChange={() => changeType(value)} />
                        {label}
                    </label>
                ))}
            </div>
        </fieldset>
    );

    const renderSalespersonPicker = () => (
        <div className={styles.field}>
            <label htmlFor="goal-targetNo" className={styles.label}>Salesperson</label>
            <select
                id="goal-targetNo"
                autoFocus
                className={styles.input}
                value={targetNo}
                onChange={(e) => chooseTarget(goalType, e.target.value)}
                {...invalidProps('targetNo')}
            >
                <option value="">Choose a salesperson…</option>
                {data.salespeople.map((s) => <option key={s.salespersonNo} value={s.salespersonNo}>{salespersonLabel(s)}</option>)}
            </select>
            {fieldError('targetNo')}
        </div>
    );

    const renderCustomerPicker = () => {
        if (selectedCustomer) {
            return (
                <div className={styles.field}>
                    <span className={styles.label}>Customer</span>
                    <div className={styles.chosenTarget}>
                        <span>{selectedCustomer.name} <span className={styles.cellNote}>{selectedCustomer.customerNo}</span></span>
                        <button type="button" className={styles.linkBtn} onClick={() => chooseTarget(goalType, '')}>Change</button>
                    </div>
                </div>
            );
        }
        const q = customerQuery.trim();
        return (
            <div className={styles.field}>
                <label htmlFor="goal-targetNo" className={styles.label}>Customer</label>
                <div className={styles.searchBox}>
                    <AiOutlineSearch className={styles.searchIcon} aria-hidden="true" />
                    <input
                        id="goal-targetNo"
                        autoFocus
                        className={styles.searchInput}
                        value={customerQuery}
                        placeholder="Name, number or city…"
                        autoComplete="off"
                        maxLength={CUSTOMER_SEARCH_MAX_LENGTH}
                        onChange={(e) => setCustomerQuery(e.target.value)}
                        onKeyDown={onCustomerSearchKeyDown}
                        {...invalidProps('targetNo')}
                    />
                </div>
                {fieldError('targetNo')}
                {customerResults.length > 0 && (
                    <ul className={styles.pickerList}>
                        {customerResults.map((c) => (
                            <li key={c.customerNo}>
                                <button type="button" className={styles.pickerItem} onClick={() => chooseTarget(goalType, c.customerNo)}>
                                    <span>{c.name}</span>
                                    <span className={styles.cellNote}>{c.customerNo}{c.city ? ` · ${c.city}, ${c.state}` : ''}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                {q && customerResults.length === 0 && <p className={styles.dialogHint}>No customers match this search.</p>}
            </div>
        );
    };

    const renderPrefillHint = () => {
        if (!targetNo) return null;
        const { lastGoal } = fields;
        return (
            <p className={styles.prefillHint}>
                {lastGoal
                    ? `Filled in from the last goal: ${formatMoney(lastGoal.targetAmount)}, ${formatDateRange(lastGoal.startDate, lastGoal.endDate)}. Keep it or change it.`
                    : 'This is the first goal here, so there is nothing to fill in from.'}
            </p>
        );
    };

    return (
        <div className={styles.dialogBackdrop} onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}>
            <form className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="goal-dialog-title" onSubmit={save} noValidate>
                <h2 id="goal-dialog-title" className={styles.panelTitle}>New goal</h2>

                {locked ? (
                    <p className={styles.dialogTarget}>
                        {lockedName()}
                        <span className={styles.cellNote}>{goalType === GOAL_TYPE.customer ? 'Customer' : 'Salesperson'} · {targetNo}</span>
                    </p>
                ) : (
                    <>
                        {renderTypePicker()}
                        {goalType === GOAL_TYPE.salesperson ? renderSalespersonPicker() : renderCustomerPicker()}
                    </>
                )}

                {renderPrefillHint()}

                <div className={styles.field}>
                    <label htmlFor="goal-targetAmount" className={styles.label}>Expected total ($)</label>
                    <input
                        id="goal-targetAmount"
                        autoFocus={locked}
                        className={styles.input}
                        inputMode="decimal"
                        autoComplete="off"
                        placeholder="0.00"
                        value={fields.targetAmount}
                        onChange={(e) => changeAmount(e.target.value)}
                        onBlur={finishAmount}
                        {...invalidProps('targetAmount')}
                    />
                    {fieldError('targetAmount')}
                </div>

                <div className={styles.dateFields}>
                    <div className={styles.field}>
                        <label htmlFor="goal-startDate" className={styles.label}>Start date</label>
                        <input
                            id="goal-startDate"
                            type="date"
                            className={styles.input}
                            min={GOAL_DATE_MIN}
                            max={fields.endDate || GOAL_DATE_MAX}
                            value={fields.startDate}
                            onChange={(e) => changeDate('startDate', e.target.value)}
                            onBlur={finishDate}
                            {...invalidProps('startDate')}
                        />
                        {fieldError('startDate')}
                    </div>
                    <div className={styles.field}>
                        <label htmlFor="goal-endDate" className={styles.label}>End date</label>
                        <input
                            id="goal-endDate"
                            type="date"
                            className={styles.input}
                            min={fields.startDate || GOAL_DATE_MIN}
                            max={GOAL_DATE_MAX}
                            value={fields.endDate}
                            onChange={(e) => changeDate('endDate', e.target.value)}
                            onBlur={finishDate}
                            {...invalidProps('endDate')}
                        />
                        {fieldError('endDate')}
                    </div>
                </div>

                {formError && <p className={styles.fieldError} role="alert">{formError}</p>}

                <div className={styles.dialogActions}>
                    <button type="button" className={styles.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
                    <button type="submit" className={styles.btnPrimary} disabled={saving}>{saving ? 'Saving…' : 'Create goal'}</button>
                </div>
            </form>
        </div>
    );
}
